import React from 'react';
import Grid from '@mui/material/GridLegacy';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography, FormControl, Select, MenuItem, FormControlLabel, Switch, Avatar } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { DIALOG_CONTENT_SX } from '../../../theme';
import { getImageDisplayUrl } from '../../../config/api';

export default function BannerDialog({
  open,
  onClose,
  form,
  setForm,
  onSubmit,
  categories = [],
  places = [],
  uploadingImage,
  onImageUpload,
}) {
  const targetType = form.targetType || form.featureType || '';
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)' } }}
    >
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        color: 'white',
        fontWeight: 700,
        py: 3,
        px: 4
      }}>
        {form._id ? 'Edit Banner' : 'Create New Banner'}
      </DialogTitle>
      <DialogContent sx={DIALOG_CONTENT_SX}>
        <Grid container spacing={3} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <Box sx={{ p: 2, borderRadius: 2, background: 'linear-gradient(135deg, rgba(240, 147, 251, 0.05) 0%, rgba(245, 87, 108, 0.05) 100%)', border: '1px solid rgba(240, 147, 251, 0.1)', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>Banner Information</Typography>
              <Typography variant="body2" color="text.secondary">Enter banner details and target information</Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Banner Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, '&:hover fieldset': { borderColor: 'primary.main' } } }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={3} label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required helperText={`${(form.description || '').length}/300 characters (minimum 10 required)`} error={(form.description || '').length > 0 && (form.description || '').length < 10} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, '&:hover fieldset': { borderColor: 'primary.main' } } }} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Image</Typography>
            <input accept="image/*" type="file" id="banner-image-upload" style={{ display: 'none' }} onChange={onImageUpload} />
            <label htmlFor="banner-image-upload">
              <Button variant="outlined" component="span" startIcon={uploadingImage ? null : <CloudUpload />} disabled={uploadingImage} sx={{ borderRadius: 2 }}>{uploadingImage ? 'Uploading...' : 'Upload image from device'}</Button>
            </label>
            {form.imageUrl && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box component="img" src={getImageDisplayUrl(form.imageUrl)} alt="Banner preview" onError={(e) => { e.target.onerror = null; e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="80"%3E%3Crect fill="%23e0e0e0" width="120" height="80"/%3E%3C/svg%3E'; }} sx={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 2, border: '1px solid', borderColor: 'divider' }} />
                <Button size="small" color="error" onClick={() => setForm((prev) => ({ ...prev, imageUrl: '' }))}>Remove</Button>
              </Box>
            )}
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ p: 2, borderRadius: 2, background: 'linear-gradient(135deg, rgba(79, 172, 254, 0.05) 0%, rgba(0, 242, 254, 0.05) 100%)', border: '1px solid rgba(79, 172, 254, 0.1)', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'info.main', mb: 0.5 }}>Target Configuration</Typography>
              <Typography variant="body2" color="text.secondary">Select what this banner will link to</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
              <Select
                label="Target Type"
                value={targetType}
                onChange={(e) => setForm({ ...form, targetType: e.target.value, featureType: e.target.value, targetId: '' })}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) return <em style={{ color: '#999' }}>Select target type</em>;
                  const labels = { place: 'Place', category: 'Category', topic: 'Topic' };
                  return labels[selected] || selected;
                }}
              >
                <MenuItem value="place">Place</MenuItem>
                <MenuItem value="category">Category</MenuItem>
                <MenuItem value="topic">Topic</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {(targetType === 'place') && (
            <Grid item xs={12}>
              <FormControl fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                <Select label="Select Place" value={form.targetId || ''} onChange={(e) => setForm({ ...form, targetId: e.target.value })} displayEmpty renderValue={(selected) => { if (!selected) return <em style={{ color: '#999' }}>Select a place</em>; const place = places.find((p) => p._id === selected); return place ? place.name : 'Select a place'; }}>
                  <MenuItem value="" disabled><em>Select a place</em></MenuItem>
                  {places.filter((p) => p.status === 'accepted').map((place) => (
                    <MenuItem key={place._id} value={place._id}>{place.name} - {place.location?.address || place.address || 'N/A'}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {(targetType === 'category') && (
            <Grid item xs={12}>
              <FormControl fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                <Select label="Select Category" value={form.targetId || ''} onChange={(e) => setForm({ ...form, targetId: e.target.value })} displayEmpty renderValue={(selected) => { if (!selected) return <em style={{ color: '#999' }}>Select a category</em>; const category = categories.find((c) => c._id === selected || c.name === selected); return category ? category.name : 'Select a category'; }}>
                  <MenuItem value="" disabled><em>Select a category</em></MenuItem>
                  {categories.filter((c) => c.isActive).map((category) => (
                    <MenuItem key={category._id} value={category._id}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar src={category.icon} alt={category.name} sx={{ width: 24, height: 24, mr: 1, bgcolor: category.color }}>{!category.icon && category.name.charAt(0).toUpperCase()}</Avatar>
                        {category.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {(targetType === 'topic') && (
            <Grid item xs={12}>
              <FormControl fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                <Select label="Select Topic" value={form.targetId || ''} onChange={(e) => setForm({ ...form, targetId: e.target.value })} displayEmpty renderValue={(selected) => (selected ? selected : <em style={{ color: '#999' }}>Select a topic</em>)}>
                  <MenuItem value="" disabled><em>Select a topic</em></MenuItem>
                  <MenuItem value="Hidden Gems">Hidden Gems</MenuItem>
                  <MenuItem value="Trending Places">Trending Places</MenuItem>
                  <MenuItem value="Recommended for You">Recommended for You</MenuItem>
                  <MenuItem value="Something Different">Something Different</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Start Date" type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="End Date" type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />} label="Active" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 3, bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
        <Button onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, px: 3 }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={
            !form.title ||
            !form.description ||
            (form.description || '').length < 10 ||
            !form.imageUrl ||
            !targetType ||
            !form.targetId
          }
          sx={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            boxShadow: '0 4px 15px rgba(240, 147, 251, 0.4)',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            '&:hover': { background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)', boxShadow: '0 6px 20px rgba(240, 147, 251, 0.5)' },
            '&:disabled': { background: 'rgba(0, 0, 0, 0.12)' }
          }}
        >
          {form._id ? 'Update Banner' : 'Create Banner'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
